import { Box, Fade } from '@mui/material';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import ImportConfigurationMetadata, { IMPORT_MODES } from './ImportConfigurationMetadata';
import ImportConfigurationPreview from './ImportConfigurationPreview';
import MDBox from 'components/MDBox';
import { useNotification } from 'hook/useNotification';
import 'filepond/dist/filepond.min.css';
import './importConfiguration.css';
import {
  useAnalyzeImportMutation,
  useImportConfigurationMutation
} from 'services/ioConfigurationApi';
import { api } from 'services/api';

registerPlugin(FilePondPluginFileValidateType);

export default function ImportConfiguration() {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();
  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzeImport] = useAnalyzeImportMutation();
  const [importConfiguration, { isLoading: isImporting }] = useImportConfigurationMutation();

  // Selection states
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [selectedLinkedEntities, setSelectedLinkedEntities] = useState({});
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [importMode, setImportMode] = useState(IMPORT_MODES.REPLACE);

  // Accordion expansion state
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const handleProcess = (fieldName, file, metadata, load, error, progress, abort) => {
    const formData = new FormData();
    formData.append('zipFile', file);

    analyzeImport(formData)
      .unwrap()
      .then((response) => {
        console.debug('Analysis result:', response);

        const transformedData = transformImportedData(response);
        setAnalysisResult(transformedData);

        // Auto-select all items
        if (transformedData.assistants) {
          setSelectedAssistants(transformedData.assistants.map((a) => a.id));
        }

        if (transformedData.linkedEntities) {
          const linkedEntitySelections = {};
          Object.entries(transformedData.linkedEntities).forEach(([type, entities]) => {
            if (type === 'linkedScripts') {
              linkedEntitySelections[type] = entities.map((e) => e.code);
            } else {
              linkedEntitySelections[type] = entities.map((e) => `${type}_${e.id ?? e.code}`);
            }
          });
          setSelectedLinkedEntities(linkedEntitySelections);
        }
        if (transformedData.prompts) {
          setSelectedPrompts(transformedData.prompts.map((p) => p.id));
        }
        if (transformedData.scripts) {
          setSelectedScripts(transformedData.scripts.map((s) => s.code));
        }

        // Initialize accordion expansion state (assistants expanded by default)
        const initialExpanded = { assistants: true };
        Object.keys(transformedData.linkedEntities || {}).forEach((entityType) => {
          initialExpanded[`linkedEntity_${entityType}`] = false;
        });
        setExpandedAccordions(initialExpanded);

        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'import.configuration.analysis.success' })
        });
        load(file.name);
      })
      .catch((err) => {
        console.error('Analysis error:', err);
        handleRemoveFile();
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'import.configuration.analysis.error' })
        });
        error('Analysis failed');
      });

    return {
      abort: () => {
        abort();
      }
    };
  };

  const handleRemoveFile = () => {
    setAnalysisResult(null);
    setSelectedAssistants([]);
    setSelectedLinkedEntities({});
    setSelectedPrompts([]);
    setSelectedScripts([]);
    setImportMode(IMPORT_MODES.REPLACE);
    setExpandedAccordions({});
    setFiles([]);
  };

  function clearCaches() {
    // Invalidate all relevant caches
    dispatch(
      api.util.invalidateTags([
        'Assistants',
        'UserAssistants',
        'UserAssistant',
        'Accounts',
        'AiModels',
        'AiProviders',
        'KnowledgeBases',
        'Prompts',
        'Scripts'
      ])
    );
  }

  function downloadImportReport(importReport) {
    // Download import report as JSON
    const blob = new Blob([JSON.stringify(importReport, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import_report_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function processImportSuccess(importReport) {
    clearCaches();
    downloadImportReport(importReport);
    handleRemoveFile();
    setFiles([]);
  }

  const handleImport = () => {
    if (!analysisResult?.metadata?.uuid) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: 'No import UUID found'
      });
      return;
    }

    const importRequest = buildImportRequest();

    importConfiguration({
      uuid: analysisResult.metadata.uuid,
      importMode,
      importRequest
    })
      .unwrap()
      .then((importReport) => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'import.configuration.success' })
        });
        processImportSuccess(importReport);
      })
      .catch((err) => {
        console.error('Import error:', err);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'import.configuration.error' })
        });
      });
  };

  const buildImportRequest = () => {
    const request = {};

    // Build assistantCodes
    if (selectedAssistants.length > 0) {
      request.assistantCodes = selectedAssistants
        .map((id) => {
          const assistant = analysisResult.assistants.find((a) => a.id === id);
          return assistant?.code;
        })
        .filter(Boolean);
    }

    // Build promptCodes
    if (selectedPrompts.length > 0) {
      request.promptCodes = selectedPrompts
        .map((id) => {
          const prompt = analysisResult.prompts.find((p) => p.id === id);
          return prompt?.code;
        })
        .filter(Boolean);
    }

    // Build scriptCodes
    if (selectedScripts.length > 0) {
      request.scriptCodes = selectedScripts;
    }

    // Build linked entities
    if (selectedLinkedEntities.accounts?.length > 0) {
      request.linkedAccountIds = selectedLinkedEntities.accounts
        .map((fullId) => {
          const id = fullId.replace('accounts_', '');
          return parseInt(id, 10);
        })
        .filter((id) => !isNaN(id));
    }

    if (selectedLinkedEntities.models?.length > 0) {
      request.linkedModelCodes = selectedLinkedEntities.models
        .map((fullId) => {
          const id = fullId.replace('models_', '');
          const model = analysisResult.linkedEntities.models.find(
            (m) => m.id === parseInt(id, 10) || m.code === id
          );
          return model?.code;
        })
        .filter(Boolean);
    }

    if (selectedLinkedEntities.providers?.length > 0) {
      request.linkedProviderCodes = selectedLinkedEntities.providers
        .map((fullId) => {
          const id = fullId.replace('providers_', '');
          const provider = analysisResult.linkedEntities.providers.find(
            (p) => p.id === parseInt(id, 10) || p.code === id
          );
          return provider?.code;
        })
        .filter(Boolean);
    }

    if (selectedLinkedEntities.knowledgeBases?.length > 0) {
      request.linkedKnowledgeBaseCodes = selectedLinkedEntities.knowledgeBases
        .map((fullId) => {
          const id = fullId.replace('knowledgeBases_', '');
          const kb = analysisResult.linkedEntities.knowledgeBases.find(
            (k) => k.id === parseInt(id, 10) || k.code === id
          );
          return kb?.code;
        })
        .filter(Boolean);
    }

    if (selectedLinkedEntities.linkedScripts?.length > 0) {
      request.linkedScriptCodes = selectedLinkedEntities.linkedScripts;
    }

    return request;
  };

  // Accordion control handlers
  const handleExpandAll = () => {
    if (!analysisResult) return;

    const allExpanded = {
      assistants: true,
      prompts: true,
      scripts: true
    };

    Object.keys(analysisResult.linkedEntities || {}).forEach((entityType) => {
      allExpanded[`linkedEntity_${entityType}`] = true;
    });

    setExpandedAccordions(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedAccordions({});
  };

  // Selection control handlers
  const handleSelectAll = () => {
    if (!analysisResult) return;

    if (analysisResult.assistants?.length > 0) {
      setSelectedAssistants(analysisResult.assistants.map((a) => a.id));
    }

    if (analysisResult.linkedEntities) {
      const allLinkedEntitiesSelections = {};
      Object.entries(analysisResult.linkedEntities).forEach(([type, entities]) => {
        if (entities?.length > 0) {
          if (type === 'linkedScripts') {
            allLinkedEntitiesSelections[type] = entities.map((e) => e.code);
          } else {
            allLinkedEntitiesSelections[type] = entities.map((e) => `${type}_${e.id ?? e.code}`);
          }
        }
      });
      setSelectedLinkedEntities(allLinkedEntitiesSelections);
    }

    if (analysisResult.prompts?.length > 0) {
      setSelectedPrompts(analysisResult.prompts.map((p) => p.id));
    }

    if (analysisResult.scripts?.length > 0) {
      setSelectedScripts(analysisResult.scripts.map((s) => s.code));
    }
  };

  const handleDeselectAll = () => {
    setSelectedAssistants([]);
    setSelectedLinkedEntities({});
    setSelectedPrompts([]);
    setSelectedScripts([]);
  };

  const handleAccordionChange = (accordionKey) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: isExpanded
    }));
  };

  return (
    <Fade in timeout={(1000, 500)}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          p: 0,
          height: '100%'
        }}
        id="importConfigurationContainer"
      >
        <MDBox
          sx={{
            border: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
            borderRadius: '5px'
          }}
        >
          <MDBox id="importFileContainer" display="flex" sx={{ width: '100%', height: '80px' }}>
            <FilePond
              files={files}
              acceptedFileTypes={['application/zip']}
              fileValidateTypeDetectType={(source, type) => {
                if (typeof source === 'string') {
                  return null;
                }
                if (source.name && source.name.toLowerCase().endsWith('.zip')) {
                  return Promise.resolve('application/zip');
                }
                return Promise.resolve(type);
              }}
              onupdatefiles={(fileItems) => {
                setFiles(fileItems);
                if (fileItems.length === 0) {
                  handleRemoveFile();
                }
              }}
              allowMultiple={false}
              maxFiles={1}
              name="zipFile"
              labelIdle={msg({ id: 'file.upload.import.configuration' })}
              className="filepond-custom"
              server={{
                process: handleProcess
              }}
            />
          </MDBox>
        </MDBox>

        {analysisResult && (
          <Box sx={{ display: 'flex', flex: 1, height: '100%' }} mt={2}>
            <Box sx={{ flex: 0.6 }}>
              <ImportConfigurationPreview
                assistants={analysisResult.assistants}
                linkedEntities={analysisResult.linkedEntities}
                prompts={analysisResult.prompts}
                scripts={analysisResult.scripts}
                selectedAssistants={selectedAssistants}
                setSelectedAssistants={setSelectedAssistants}
                selectedLinkedEntities={selectedLinkedEntities}
                setSelectedLinkedEntities={setSelectedLinkedEntities}
                selectedPrompts={selectedPrompts}
                setSelectedPrompts={setSelectedPrompts}
                selectedScripts={selectedScripts}
                setSelectedScripts={setSelectedScripts}
                expandedAccordions={expandedAccordions}
                onAccordionChange={handleAccordionChange}
              />
            </Box>
            <Box
              sx={{
                flex: 0.4,
                minWidth: '350px',
                position: 'sticky',
                height: '500px',
                top: '10px'
              }}
            >
              <ImportConfigurationMetadata
                metadata={analysisResult.metadata}
                importMode={importMode}
                onImportModeChange={setImportMode}
                onCancel={handleRemoveFile}
                onImport={handleImport}
                isImporting={isImporting}
                onExpandAll={handleExpandAll}
                onCollapseAll={handleCollapseAll}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );

  function transformImportedData(response) {
    const linkedEntities = {};

    if (response.linkedAccounts && response.linkedAccounts.length > 0) {
      linkedEntities.accounts = response.linkedAccounts.map((account) => ({
        ...account,
        code: account.code || `account_${account.id}`,
        label: account.label || account.code || `Account ${account.id}`
      }));
    }

    if (response.linkedKnowledgeBases && response.linkedKnowledgeBases.length > 0) {
      linkedEntities.knowledgeBases = response.linkedKnowledgeBases;
    }

    if (response.linkedModels && response.linkedModels.length > 0) {
      linkedEntities.models = response.linkedModels;
    }

    if (response.linkedProviders && response.linkedProviders.length > 0) {
      linkedEntities.providers = response.linkedProviders;
    }

    if (response.linkedScripts && response.linkedScripts.length > 0) {
      linkedEntities.linkedScripts = response.linkedScripts.map((script) => ({
        ...script,
        label: script.code ? script.code.toLowerCase() : ''
      }));
    }

    const enrichedAssistants = (response.assistants || []).map((assistant) => {
      const dependencies = extractAssistantDependencies(assistant);
      return {
        ...assistant,
        dependencies
      };
    });

    return {
      metadata: response.metadata || {},
      assistants: enrichedAssistants,
      linkedEntities,
      prompts: response.prompts || [],
      scripts: response.scripts || []
    };
  }

  function extractAssistantDependencies(assistant) {
    if (assistant.dependencies && Array.isArray(assistant.dependencies)) {
      const groupedDeps = {};

      assistant.dependencies.forEach((dep) => {
        const entityType = dep.entityType;

        const typeMap = {
          account: 'accounts',
          model: 'models',
          provider: 'providers',
          knowledgeBase: 'knowledgeBases',
          script: 'linkedScripts'
        };

        const frontendType = typeMap[entityType] || entityType;

        if (!groupedDeps[frontendType]) {
          groupedDeps[frontendType] = [];
        }

        if (entityType === 'script' && dep.code) {
          groupedDeps[frontendType].push(dep.code);
        } else if (dep.id !== null && dep.id !== undefined) {
          groupedDeps[frontendType].push(dep.id);
        }
      });

      return groupedDeps;
    }

    return {};
  }
}
